/**
 * Which pose source is in force right now, and when it may change.
 *
 * `poseSourceRouter` decides from a snapshot. This decides WHEN to take a new
 * snapshot — which is a separate problem, and the reason it is separate is that
 * the two triggers can fight:
 *
 *   the device is slow            -> go to the server
 *   the network is slow           -> come back to the device
 *
 * If neither improves, a naive re-evaluation oscillates every few hundred
 * milliseconds. That is maximal jitter, and jitter rather than mean rate is what
 * defeats the gate (plan §2.4a: a steady 2.3 Hz beat a bursty 4.2 Hz, with
 * `correctPostureLostCount` going 11 -> 0). **A flapping source is worse than
 * either source alone.**
 *
 * So the source is bounded two ways: at most one switch in each direction per
 * attempt, and pinned outright once the rotation starts.
 */

import {
  decidePoseSource,
  type PoseSource,
  type ProbeState,
} from "./poseSourceRouter";
import type { PoseRemoteConfig } from "./poseRemoteConfig";
import type { DeviceClass } from "./alignmentTuning";
import type { PoseSourceOverride } from "./poseSourceOverride";

/**
 * One device -> server and one server -> device. A third change inside one
 * attempt is the oscillation this class exists to prevent, not new information.
 */
const MAX_SWITCHES_PER_ATTEMPT = 2;

export type PoseSourceReason =
  | ReturnType<typeof decidePoseSource>["reason"]
  | "network-degraded"
  /** The cloud could not see a gated joint, so the attempt went to the phone. */
  | "server-joint-unseen"
  | "flap-guard"
  /** TEMPORARY, dev only: a tester picked the source on the scan screen. */
  | "forced";

export interface PoseSourceDecisionOut {
  source: PoseSource;
  reason: PoseSourceReason;
}

export interface PoseSourceControllerDeps {
  getConfig: () => PoseRemoteConfig;
  getDeviceClass: () => DeviceClass;
  hasModelFailed: () => boolean;
  /** The server round trip is too slow to be worth using. */
  isNetworkDegraded: () => boolean;
  /** The on-device detector is failing on THIS attempt. */
  isDeviceDegraded: () => boolean;
  isOnline: () => boolean;
  getProbe: () => ProbeState;
  /** The startup benchmark's verdict, from the session device verdict. */
  getBenchmarkVerdict: () => "pass" | "fail" | "unknown";
  /** Live: the phone's detector broke this attempt. */
  isDetectorBroken: () => boolean;
  /** Live: blind while the person is clearly in frame. */
  isBlindWhileVisible: () => boolean;
  /** Live: the model that could not see a gated joint this attempt, if any (liveHealth.ts). */
  getJointUnseenSource: () => PoseSource | null;
  /** TEMPORARY, dev only: the scan screen pill's forced source, or "auto". */
  getOverride: () => PoseSourceOverride;
}

/**
 * Reasons that mean the PHONE failed live — what session memory remembers.
 * `joint-unseen` is the phone's own miss; `server-joint-unseen` is the cloud's,
 * says nothing against the phone, and is deliberately absent.
 */
const LIVE_REASONS = ["live-degraded", "detector-broken", "blind-while-visible", "joint-unseen"];

export class PoseSourceController {
  private deps: PoseSourceControllerDeps;
  private last: PoseSourceDecisionOut | null = null;
  private switches = 0;
  private pinned: PoseSourceDecisionOut | null = null;
  /** A live trigger switched an attempt this page load. Only a reload clears it. */
  private sessionDemoted = false;
  /** The first live reason seen this attempt, for the summary row. */
  private attemptLiveReason: string | null = null;

  constructor(deps: PoseSourceControllerDeps) {
    this.deps = deps;
  }

  /**
   * Pin the current source for the rest of the rotation.
   *
   * Switching mid-spin cannot be recovered from. The rotation is 44 fixed
   * 250 ms ticks and `ScanUploader` derives its 22 uploaded frames from them
   * one-for-one, so changing where poses come from part-way through risks the
   * scan itself rather than merely its quality. Plan §3.6: do not fall back
   * mid-rotation; finish on `Unknown` ticks instead.
   *
   * The pin keeps the decision's REASON as well as its source. The countdown
   * starts a moment before the alignment summary is written, so a pin that
   * said only "pinned" erased why the attempt ran where it did on every
   * attempt that reached the rotation. `isPinned()` says that it is frozen.
   */
  freeze(): void {
    this.pinned = this.current();
  }

  isPinned(): boolean {
    return this.pinned !== null;
  }

  /** Called at each ALIGNMENT entry. Thaws the pin and refunds the budget. */
  resetAttempt(): void {
    this.pinned = null;
    this.last = null;
    this.switches = 0;
    // Not `sessionDemoted` — surviving the attempt is the point of it.
    this.attemptLiveReason = null;
  }

  isSessionDemoted(): boolean {
    return this.sessionDemoted;
  }

  getAttemptLiveReason(): string | null {
    return this.attemptLiveReason;
  }

  /**
   * The decision for the frame being routed NOW, and the only place a decision
   * is recorded: the switch budget, session memory and the attempt's live
   * reason all change here. Call it only when a frame is about to be sent
   * (`serverPoseService.shouldUseServer`). Anything that merely wants to know
   * the answer — the dev pill, a summary row — reads `peek()`.
   */
  current(): PoseSourceDecisionOut {
    if (this.pinned) return this.pinned;

    // TEMPORARY, dev only (poseSourceOverride.ts). Forced means forced: no
    // router, no network fallback, no flap guard, no session memory — a tester
    // comparing the two sources has to see the one they picked, failures
    // included. Still below the pin, so the rotation stays unswitchable.
    const override = this.deps.getOverride();
    if (override !== "auto") {
      this.last = { source: override, reason: "forced" };
      return this.last;
    }

    const next = this.decide();

    if (this.last && next.source !== this.last.source) {
      if (this.switches >= MAX_SWITCHES_PER_ATTEMPT) {
        // Budget spent. Hold the last decision rather than keep changing —
        // both triggers stuck on is a real state (a slow phone on a slow
        // network) and is not new information every frame.
        return { source: this.last.source, reason: "flap-guard" };
      }
      this.switches++;
    }

    if (LIVE_REASONS.indexOf(next.reason) !== -1) {
      this.sessionDemoted = true;
      if (this.attemptLiveReason === null) this.attemptLiveReason = next.reason;
    }

    this.last = next;
    return next;
  }

  /**
   * What `current()` would return, with nothing recorded.
   *
   * Reads must not route. The dev pill polls twice a second and every summary
   * row reads once; through `current()` a "detector broken" left over from boot
   * was read on the scan screen before any scan on the 2026-09-14 dev build,
   * and session memory moved the whole page load to the cloud.
   */
  peek(): PoseSourceDecisionOut {
    if (this.pinned) return this.pinned;
    const override = this.deps.getOverride();
    if (override !== "auto") return { source: override, reason: "forced" };

    const next = this.decide();
    if (
      this.last &&
      next.source !== this.last.source &&
      this.switches >= MAX_SWITCHES_PER_ATTEMPT
    ) {
      return { source: this.last.source, reason: "flap-guard" };
    }
    return next;
  }

  /** The router plus the network fallback. Reads state, never writes it. */
  private decide(): PoseSourceDecisionOut {
    const config = this.deps.getConfig();
    const modelsFailed = this.deps.hasModelFailed();
    const detectorBroken = this.deps.isDetectorBroken();
    const benchmarkVerdict = this.deps.getBenchmarkVerdict();
    // Only when the flag lets the verdict act: off, the check is measure-only.
    const keypointsKnownWrong = benchmarkVerdict === "fail" && config.onBenchmarkFail;
    const jointUnseenSource = this.deps.getJointUnseenSource();

    const decision = decidePoseSource({
      modelsFailed,
      deviceClass: this.deps.getDeviceClass(),
      liveDegraded: this.deps.isDeviceDegraded(),
      probe: this.deps.getProbe(),
      online: this.deps.isOnline(),
      config,
      benchmarkVerdict,
      detectorBroken,
      blindWhileVisible: this.deps.isBlindWhileVisible(),
      jointUnseen: jointUnseenSource === "device",
      sessionDemoted: this.sessionDemoted,
    });

    let next: PoseSourceDecisionOut = decision;

    // The network fallback sits OUTSIDE the router on purpose: the router
    // answers "where should this attempt run", and this answers "is the answer
    // still usable". Skipped when the phone has no working path — models that
    // never loaded, a detector that broke, or a startup check that proved its
    // keypoints wrong — because "fall back" would mean falling back to nothing
    // usable. A slow server is the only option they have. The last case is
    // about the phone, not about which rule chose the server: on 2026-09-14 a
    // phone that failed the check fell back at an 861 ms round trip and its
    // rotation failed on its own keypoints.
    if (
      next.source === "server" &&
      !modelsFailed &&
      !detectorBroken &&
      !keypointsKnownWrong &&
      this.deps.isNetworkDegraded()
    ) {
      next = { source: "device", reason: "network-degraded" };
    }

    // The cloud could not see a gated joint: give the phone's own model its
    // one try. Outside the router for the same reason as the network fallback
    // above — it is about whether the answer is still usable — and with the
    // same three exceptions, because each of them means the phone has no model
    // worth trying. Both Androids tested on 2026-09-16 were the third kind:
    // their startup check failed, and their own model never found the wrists
    // or ankles when forced.
    //
    // One try, not a round trip: the signal latches on the first model that
    // tripped, so once this fires the phone is never judged this attempt, and
    // a phone that tripped first sends the attempt the other way and is never
    // judged again either.
    if (
      next.source === "server" &&
      config.onJointUnseen &&
      jointUnseenSource === "server" &&
      !modelsFailed &&
      !detectorBroken &&
      !keypointsKnownWrong
    ) {
      next = { source: "device", reason: "server-joint-unseen" };
    }

    return next;
  }
}
